import "./VideoItem.scss";
import { useRef, useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Video, VideoPlayButton, Picture } from "/App.Components";
import { VideoProps } from "/Models/Generated/VideoProps";
import { createSpreadableBem } from "/Helpers/createBem";
import { useModal } from "/Components/Shared/ModalProvider/ModalProvider";
import FillViewportKeepAspectRatio from "/Helpers/FillViewportKeepAspectRatio";

interface Props extends VideoProps {
  id: number;
  activeVideoId: number;
  setActiveVideoId: (id: number) => void;
}

const VideoItem = (props: Props) => {
  const {
    videoUrl,
    ariaLabel,
    description,
    duration,
    durationAriaLabel,
    id,
    activeVideoId,
    setActiveVideoId,
    posterImage,
  } = props;

  const [initialized, setInitialized] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [previewBox, setPreviewBox] = useState<HTMLDivElement | null>(null);
  const [modalSlot, setModalSlot] = useState<HTMLDivElement | null>(null);

  const { openModal } = useModal();
  const thisRef = useRef<HTMLButtonElement>(null);
  const modalSlotRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoNode, setVideoNode] = useState<HTMLVideoElement | null>(null);

  const insideModal = !!modalSlot;
  const portalTarget = modalSlot ?? previewBox;
  const showVideo = !!videoUrl && (initialized || insideModal);
  const openVideoLabel = ariaLabel?.trim() || "Spill av video";
  const metadataLabel = [
    description?.trim(),
    (durationAriaLabel ?? duration)?.trim(),
  ]
    .filter(Boolean)
    .join(", ");
  const buttonAriaLabel = metadataLabel
    ? `${openVideoLabel}: ${metadataLabel}`
    : openVideoLabel;

  const handleVideoRef = useCallback((node: HTMLVideoElement | null) => {
    videoRef.current = node;
    setVideoNode(node);
  }, []);

  const handleModalSlot = useCallback((el: HTMLDivElement | null) => {
    modalSlotRef.current = el;
    setModalSlot(el);
  }, []);

  const bem = createSpreadableBem("video-item-norad");

  const posterUrl = posterImage?.srcSet?.[0]?.srcSet
    ?.split(",")[0]
    ?.trim()
    .split(" ")[0];

  useEffect(() => {
    if (!insideModal || !modalSlotRef.current || !thisRef.current) return;
    const adjustWidth = () => {
      FillViewportKeepAspectRatio(70, 112, modalSlotRef, thisRef);
    };
    adjustWidth();
    window.addEventListener("resize", adjustWidth);
    return () => window.removeEventListener("resize", adjustWidth);
  }, [insideModal]);

  useEffect(() => {
    const mql = window.matchMedia("(pointer: coarse)");
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener("change", update);
    return () => mql.removeEventListener("change", update);
  }, []);

  // Auto-initialize the very first video when it appears in the screen
  useEffect(() => {
    if (id === 0 && activeVideoId === 0 && videoNode) {
      setInitialized(true);

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            const tryPlay = async () => {
              try {
                await videoNode.play();
              } catch (err) {
                console.warn("Autoplay failed on first video:", err);
              }
            };

            if (videoNode.readyState >= 2) {
              tryPlay();
            } else {
              videoNode.addEventListener("loadeddata", tryPlay, { once: true });
            }
          }
        },
        { threshold: 0 },
      );

      observer.observe(videoNode);
      return () => observer.disconnect();
    }
  }, [videoNode]);

  useEffect(() => {
    if (activeVideoId === id && !initialized) {
      setInitialized(true);
    }
  }, [activeVideoId, id, initialized]);

  // Hide the poster overlay only once the video has actual frames decoded
  useEffect(() => {
    if (!videoNode) return;
    const onPlaying = () => setVideoPlaying(true);
    videoNode.addEventListener("playing", onPlaying);
    return () => videoNode.removeEventListener("playing", onPlaying);
  }, [videoNode]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (insideModal) {
      video.play().catch(() => {});
      return;
    }

    if (activeVideoId === id) {
      const tryPlay = async () => {
        try {
          await video.play();
        } catch {
          setInitialized(false);
        }
      };

      if (video.readyState >= 2) {
        tryPlay();
      } else {
        video.addEventListener("loadeddata", tryPlay, { once: true });
      }
    } else {
      video.pause();
    }
  }, [activeVideoId, insideModal, id]);

  const handleOpenModal = () => {
    if (isMobile && videoRef.current) {
      const el = videoRef.current;
      el.muted = false;
      el.controls = true;

      const playPromise = el.play();
      const enterFullscreen = () => {
        const requestFullScreen =
          el.requestFullscreen ||
          (el as any).webkitEnterFullscreen ||
          (el as any).webkitRequestFullscreen;
        if (!requestFullScreen) return;
        const result = requestFullScreen.call(el);
        if (result && typeof result.catch === "function") {
          result.catch(() => {});
        }
      };

      if (playPromise && typeof playPromise.then === "function") {
        playPromise.then(enterFullscreen).catch(enterFullscreen);
      } else {
        enterFullscreen();
      }
      return;
    }

    if (videoRef.current) {
      videoRef.current.pause();
    }

    openModal(<div {...bem("__modal-wrapper")} ref={handleModalSlot} />);
  };

  const videoElement = videoUrl && (
    <Video
      videoUrl={videoUrl}
      muted={!insideModal}
      playsInline
      preload={insideModal || activeVideoId === id ? "auto" : "metadata"}
      poster={posterUrl}
      controls={insideModal}
      aspect="9/16"
      ref={handleVideoRef}
      disableFullscreen={!isMobile && insideModal}
    />
  );

  return (
    <button
      tabIndex={0}
      aria-label={buttonAriaLabel}
      data-video-id={id}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleOpenModal();
        }
      }}
      {...bem("", { "--inside-modal": insideModal })}
      ref={thisRef}
      onClick={handleOpenModal}
      onMouseEnter={() => {
        if (!insideModal && !isMobile) {
          setActiveVideoId(id);
          setInitialized(true);
        }
      }}
      onTouchStart={() => {
        if (!insideModal && isMobile) {
          setActiveVideoId(id);
          setInitialized(true);
        }
      }}
    >
      <div {...bem("__duration")}>
        <VideoPlayButton />
        <span>{duration}</span>
      </div>

      <div {...bem("__preview-box")} ref={setPreviewBox}>
        {posterImage && (insideModal || !videoPlaying) && (
          <div {...bem("__poster-overlay")}>
            <Picture
              {...posterImage}
              pictureAspectRatio="9/16"
              pictureBehaviour="Cover"
            />
          </div>
        )}
      </div>

      {description && <div {...bem("__description-gradient")}></div>}
      {description && (
        <div {...bem("__description-wrapper")}>
          <span {...bem("__description")}>{description}</span>
        </div>
      )}

      {portalTarget && showVideo && createPortal(videoElement, portalTarget)}
    </button>
  );
};

export default VideoItem;
